/**
 * Event status lifecycle controls (DRAFT → OPEN → CLOSED → COMPLETED).
 *
 * EventStatusControls: read-only status badge + a visible primary "next step" button, with the
 * less common backward moves in a "⋯" menu. The confirmation flow lives in useEventStatusTransition.
 */

import { MoreHorizontal } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  getAllowedStatusTransitions,
  getStatusBadgeVariant,
  getStatusLabel,
} from '@/models/event';
import type { Event, EventStatus } from '@/types/event.types';
import { NEXT_STATUS, NO_PERMISSION_HINT, statusActionLabel } from '../utils/statusActions';

interface EventStatusControlsProps {
  event: Event;
  canEdit: boolean;
  onRequest: (target: EventStatus) => void;
  disabled?: boolean;
}

export function EventStatusControls({ event, canEdit, onRequest, disabled }: EventStatusControlsProps) {
  const status = event.status as EventStatus;
  const next = NEXT_STATUS[status];
  const others = getAllowedStatusTransitions(status).filter((s) => s !== next);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={getStatusBadgeVariant(status)}>{getStatusLabel(status)}</Badge>

      {next && (
        <span title={canEdit ? undefined : NO_PERMISSION_HINT}>
          <Button
            size="sm"
            variant={status === 'DRAFT' ? 'default' : 'outline'}
            disabled={!canEdit || disabled}
            onClick={() => onRequest(next)}
          >
            {statusActionLabel(status, next)}
          </Button>
        </span>
      )}

      {canEdit && others.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8" disabled={disabled} aria-label="Más cambios de estado">
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Cambiar estado</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {others.map((s) => (
              <DropdownMenuItem key={s} onClick={() => onRequest(s)}>
                {statusActionLabel(status, s)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}
